import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { EventBusy } from "@mui/icons-material";

import { EventsStationSortColumn } from "@framework/types";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { RangeType, useAsu, toFakeUtc } from "@framework/provider-asu";
import { useApiCall } from "@framework/react-hooks";
import { SortDirection } from "@framework/types-collection";
import { ConfirmationDialog } from "@framework/mui-dialog-confirmation";

import { EmptyPlaceholder } from "../../../../components/common/empty-placeholder";
import { SOUND_ALARM_REFRESH_EVENT } from "../../../../components/common/header/sound-alarm/constants";
import {
  IResponseEventsLog,
  IEventsPageResponse,
} from "../../../dashboard/tabs/scheme/components/events-train/interfaces";
import { useEventType } from "../../../../utils/event";
import { SortButton } from "../../../../components/common/buttons/sort-button";
import { DataExportButton } from "../../../../components/common/buttons/export-button";
import { downloadCsv } from "../../../../utils/download-csv";
import { useFormatDateHumanReadable } from "../../../../hooks";
import { Row } from "./row";
import { StyledParentHeaderCollapseTableCell, StyledParentHeaderTableCell } from "./row/styled";
import { mapEventsToGroupedLogs } from "./mappers";

const TAKE_NORMAL = 50;
const TAKE_RECORDER = 100;
const HEADERS_ID = "pages.eventsLog.headers";
export const MESSAGES_HEADERS_ID = "pages.eventsLog.messages.headers";

export interface IEventLog {
  id: string;
  status: number;
  position: string;
  rank: string;
  date: string;
  messages: IEventMessage[];
}

interface IEventMessage {
  idEvent: number;
  ackStatus: number;
  status: number;
  type: string;
  pointName: string;
  date: string;
  parameter: string;
  source: string;
  fullName: string;
  message: string;
  defect: string;
}

interface IRequestStationEventsLog {
  id: number;
  loadServiceEvents: boolean;
  skip: number;
  take: number;
  sortColumn?: EventsStationSortColumn;
  sortOrder?: SortDirection;
  startOverride?: string;
}

export const StationEventsTable: FC = () => {
  const {
    isLoading,
    selectedItem,
    startTimestamp,
    endTimestamp,
    dbTz,
    userSelectedRangeType,
    recorderTickId,
    recorderWindowStart,
    userSelectedRecorderModePeriod,
    recorderModeInterval,
  } = useAsu();

  const isRecorderMode = userSelectedRangeType === RangeType.RECORDER_MODE;
  const stationId = selectedItem && "Id" in selectedItem ? selectedItem.Id : null;

  const [rawEvents, setRawEvents] = useState<IResponseEventsLog[]>([]);
  const [loadServiceEvents, setLoadServiceEvents] = useState<boolean>(false);
  const [skip, setSkip] = useState<number>(0);
  const take = isRecorderMode ? TAKE_RECORDER : TAKE_NORMAL;
  const [sortColumn, setSortColumn] = useState<EventsStationSortColumn>(EventsStationSortColumn.EventLogDate);
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirection.desc);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);

  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [ackEventId, setAckEventId] = useState<number | null>(null);

  const rawEventsLengthRef = useRef(0);
  rawEventsLengthRef.current = rawEvents.length;

  const recorderFetchGenRef = useRef(0);

  const { formatMessage } = useIntl();
  const formatDate = useFormatDateHumanReadable();
  const { getEventType, getMessage } = useEventType();

  const recorderWindowStartRef = useRef(recorderWindowStart);
  recorderWindowStartRef.current = recorderWindowStart;

  const endTimestampRef = useRef(endTimestamp);
  endTimestampRef.current = endTimestamp;

  const dbTzRef = useRef(dbTz);
  dbTzRef.current = dbTz;

  // refs justLeftRecorderModeRef и prevIsRecorderModeRef для того, чтобы не запускать эффект при первом рендере после выхода из recorder mode.
  const justLeftRecorderModeRef = useRef(false);
  const prevIsRecorderModeRef = useRef(isRecorderMode);

  if (prevIsRecorderModeRef.current !== isRecorderMode) {
    prevIsRecorderModeRef.current = isRecorderMode;
    if (!isRecorderMode) {
      justLeftRecorderModeRef.current = true;
    }
  }

  const trimRawEventsToWindow = useCallback((rows: IResponseEventsLog[]): IResponseEventsLog[] => {
    const wStr = recorderWindowStartRef.current;
    const leftMs = wStr ? new Date(wStr).getTime() : null;
    if (leftMs === null || Number.isNaN(leftMs)) return rows;

    const endMs = new Date(endTimestampRef.current).getTime();
    if (!Number.isNaN(endMs) && leftMs >= endMs) {
      return rows;
    }
    const tz = dbTzRef.current;
    return rows.filter(e => {
      const t = toFakeUtc(e.EventLogDate, tz).getTime();
      return !Number.isNaN(t) && t >= leftMs;
    });
  }, []);

  const events = useMemo(
    () =>
      mapEventsToGroupedLogs(rawEvents, {
        formatDate,
        formatMessage,
        getEventType,
        getMessage,
      }),
    [rawEvents, formatDate, formatMessage, getEventType, getMessage],
  );

  const recorderRawForDisplay = useMemo((): IResponseEventsLog[] => {
    if (!isRecorderMode) return [];
    if (recorderTickId === 1) return rawEvents;

    return trimRawEventsToWindow(rawEvents);
  }, [isRecorderMode, rawEvents, recorderTickId, recorderWindowStart, endTimestamp, trimRawEventsToWindow]);

  const recorderTableEvents = useMemo(
    () =>
      isRecorderMode
        ? mapEventsToGroupedLogs(recorderRawForDisplay, {
            formatDate,
            formatMessage,
            getEventType,
            getMessage,
          })
        : [],
    [isRecorderMode, recorderRawForDisplay, formatDate, formatMessage, getEventType, getMessage],
  );

  const displayEvents = isRecorderMode ? recorderTableEvents : events;

  const handleExport = (): void => {
    const exportRows = isRecorderMode ? recorderRawForDisplay : rawEvents;
    if (stationId && exportRows.length) {
      const date = new Date().toISOString().split("T")[0];
      const fileName = `station-events-report-${date}.csv`;
      downloadCsv(
        exportRows.map(event => ({
          status: event.DataAlarmStatus || "-",
          position: event.TrainPosition,
          rank: event.EquipmentGroupName || "-",
          date: formatDate(event.EventLogDate),
          ackStatus: formatMessage({ id: `enums.ackStatus.${event.Flags}` }),
          type: getEventType({ type: event.EventType, subType: event.EventSubType }),
          pointName: event.PointName,
          parameter:
            typeof event.MeasureType === "number"
              ? formatMessage({ id: `pages.journals.popup.train.points.${event.MeasureType}` })
              : "",
          source: "",
          fullName: event.UserLogin || "",
          message: getMessage(event),
          defect: "",
        })),
        fileName,
        [
          { field: "status", title: formatMessage({ id: `${HEADERS_ID}.status` }) },
          { field: "position", title: formatMessage({ id: `${HEADERS_ID}.position` }) },
          { field: "rank", title: formatMessage({ id: `${HEADERS_ID}.rank` }) },
          { field: "date", title: formatMessage({ id: `${HEADERS_ID}.dateTime` }) },
          { field: "ackStatus", title: formatMessage({ id: `${MESSAGES_HEADERS_ID}.ackStatus` }) },
          { field: "type", title: formatMessage({ id: `${MESSAGES_HEADERS_ID}.eventType` }) },
          { field: "pointName", title: formatMessage({ id: `${MESSAGES_HEADERS_ID}.point` }) },
          { field: "parameter", title: formatMessage({ id: `${MESSAGES_HEADERS_ID}.parameter` }) },
          { field: "source", title: formatMessage({ id: `${MESSAGES_HEADERS_ID}.source` }) },
          { field: "fullName", title: formatMessage({ id: `${MESSAGES_HEADERS_ID}.fullName` }) },
          { field: "message", title: formatMessage({ id: `${MESSAGES_HEADERS_ID}.message` }) },
          { field: "defect", title: formatMessage({ id: `${MESSAGES_HEADERS_ID}.defect` }) },
        ],
      );
    }
  };

  const { fn: getEventLog, isLoading: isGettingEventLog } = useApiCall<IEventsPageResponse[]>(
    (api, { id, loadServiceEvents, skip, take, sortColumn, sortOrder, startOverride }: IRequestStationEventsLog) => {
      const data = {
        idTopLevel: id,
        loadServiceEvents,
        startTimestamp: startOverride ?? startTimestamp,
        endTimestamp,
        skip,
        take,
        ...(sortColumn ? { sortColumn } : {}),
        ...(sortOrder ? { sortOrder } : {}),
      };

      // for Train events we use: services/ui/src/pages/dashboard/tabs/scheme/components/events-train/index.tsx
      return api.fetchJson({
        url: `/events/station`,
        data,
      }) as Promise<IEventsPageResponse[]>;
    },
    { success: false },
  );

  const handleSort = (column: EventsStationSortColumn) => () => {
    if (column !== sortColumn) {
      setSortColumn(column);
      setSortOrder(SortDirection.asc);
      return;
    }

    setSortOrder(prev => (prev === SortDirection.asc ? SortDirection.desc : SortDirection.asc));
  };

  const loadMore = () => setSkip(prev => prev + take);

  const { fn: setAckStatus, isLoading: isAcknowledging } = useApiCall(
    (api, values: { ackEventId: number }) => {
      const { ackEventId } = values;
      return (
        api.fetchJson({
          url: `/events/ack-event/${ackEventId}`,
          method: "PUT",
        }) as Promise<unknown>
      )
        .then(() => {
          window.dispatchEvent(new Event(SOUND_ALARM_REFRESH_EVENT));

          return getEventLog(undefined, {
            id: stationId,
            loadServiceEvents,
            skip,
            take,
            sortColumn,
            sortOrder,
          });
        })
        .then((json: unknown) => {
          const page = json as IEventsPageResponse | null | undefined;
          const rows = page?.items;
          if (!rows?.length) return;
          setRawEvents(rows);
        })
        .catch(error => {
          console.error("Error acknowledging event:", error);
          throw error;
        })
        .finally(() => {
          setAckDialogOpen(false);
          setAckEventId(null);
        });
    },
    { success: false },
  );

  const loading = (isLoading && !isRecorderMode) || isGettingEventLog;

  useEffect(() => {
    if (!isRecorderMode) return;
    setRawEvents([]);
    setSkip(0);
    setIsLastPage(false);
  }, [stationId, isRecorderMode, userSelectedRecorderModePeriod, recorderModeInterval]);

  useEffect(() => {
    if (!stationId || !isRecorderMode || !recorderTickId) return;

    const fetchGen = ++recorderFetchGenRef.current;

    const useFullRecorderWindow = isRecorderMode && (recorderTickId === 1 || rawEventsLengthRef.current === 0);
    const startOverride = useFullRecorderWindow ? recorderWindowStart ?? startTimestamp : undefined;

    void getEventLog(undefined, {
      id: stationId,
      loadServiceEvents,
      skip: 0,
      take,
      sortColumn,
      sortOrder,
      ...(startOverride !== undefined ? { startOverride } : {}),
    }).then((json: unknown) => {
      if (fetchGen !== recorderFetchGenRef.current) return;

      const page = json as IEventsPageResponse | null | undefined;
      const rows = page?.items ?? [];

      if (rows.length === 0) {
        if (recorderTickId === 1) {
          setRawEvents([]);
        }
        return;
      }

      if (recorderTickId === 1) {
        setRawEvents(rows);
        return;
      }

      setRawEvents(prev => {
        const newItems = rows.filter(item => !prev.some(prevItem => prevItem.idEvent === item.idEvent));
        return [...prev, ...newItems];
      });
    });
  }, [stationId, loadServiceEvents, isRecorderMode, recorderTickId, recorderWindowStart, startTimestamp, endTimestamp]);

  useEffect(() => {
    if (isRecorderMode || !stationId || !skip) return;

    void getEventLog(undefined, {
      id: stationId,
      loadServiceEvents,
      skip,
      take,
      sortColumn,
      sortOrder,
    }).then((json: unknown) => {
      const page = json as IEventsPageResponse | null | undefined;
      const rows = page?.items;
      if (!rows?.length) {
        setIsLastPage(true);
        return;
      }
      const newRawEvents = [...rawEvents, ...rows];
      setRawEvents(newRawEvents);
      setIsLastPage((page?.page ?? 0) >= (page?.pagesCount ?? 0) - 1);
    });
  }, [skip, isRecorderMode]);

  useEffect(() => {
    if (!stationId || isRecorderMode) return;

    if (justLeftRecorderModeRef.current) {
      justLeftRecorderModeRef.current = false;
      return;
    }

    setRawEvents([]);
    setIsLastPage(false);
    setSkip(0);

    void getEventLog(undefined, {
      id: stationId,
      loadServiceEvents,
      skip: 0,
      take,
      sortColumn,
      sortOrder,
    }).then((json: unknown) => {
      const page = json as IEventsPageResponse | null | undefined;
      const rows = page?.items;
      if (!rows?.length) return;
      setRawEvents(rows);
      setIsLastPage((page?.page ?? 0) >= (page?.pagesCount ?? 0) - 1);
    });
  }, [stationId, startTimestamp, endTimestamp, loadServiceEvents, isRecorderMode, sortColumn, sortOrder]);

  const noEvents = formatMessage({ id: "pages.scheme.events.noEvents" });

  return (
    <>
      <ConfirmationDialog
        open={ackDialogOpen}
        onCancel={() => setAckDialogOpen(false)}
        onConfirm={() => {
          if (ackEventId) {
            void setAckStatus(undefined, { ackEventId });
          }
        }}
        maxWidth="xs"
        disabled={isAcknowledging}
        message="pages.eventsLog.ack.confirmTitle"
      >
        <ProgressOverlay isLoading={isAcknowledging}>
          <FormattedMessage id="pages.eventsLog.ack.confirmText" />
        </ProgressOverlay>
      </ConfirmationDialog>
      <ProgressOverlay isLoading={loading} wrapperSx={{ height: "100%" }}>
        <Box sx={{ display: "flex", pr: 2, justifyContent: "flex-end", alignItems: "center" }}>
          <Checkbox checked={loadServiceEvents} onChange={e => setLoadServiceEvents(e.currentTarget.checked)} />
          <Typography sx={{ marginRight: "1rem" }}>{formatMessage({ id: "pages.eventsLog.serviceEvents" })}</Typography>
          {!!displayEvents?.length && <DataExportButton handleExport={() => void handleExport()} />}
        </Box>
        {displayEvents?.length ? (
          <>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ borderLeft: "1px solid #EBEBF7", borderTop: "1px solid #EBEBF7", height: "100%" }}
            >
              <Table aria-label="events-log" sx={{ tableLayout: "fixed" }} stickyHeader>
                <TableHead>
                  <TableRow sx={{ "& > *": { background: "#FAFAFF", borderRight: "1px solid #EBEBF7" } }}>
                    <StyledParentHeaderCollapseTableCell />
                    <StyledParentHeaderTableCell sx={{ width: "24%" }}>
                      {formatMessage({ id: `${HEADERS_ID}.status` })}
                      {!isRecorderMode && (
                        <SortButton
                          direction={sortOrder}
                          onClick={handleSort(EventsStationSortColumn.DataAlarmStatus)}
                          isActive={sortColumn === EventsStationSortColumn.DataAlarmStatus}
                        />
                      )}
                    </StyledParentHeaderTableCell>
                    <StyledParentHeaderTableCell sx={{ width: "24%" }}>
                      {formatMessage({ id: `${HEADERS_ID}.position` })}
                      {!isRecorderMode && (
                        <SortButton
                          direction={sortOrder}
                          onClick={handleSort(EventsStationSortColumn.TrainPosition)}
                          isActive={sortColumn === EventsStationSortColumn.TrainPosition}
                        />
                      )}
                    </StyledParentHeaderTableCell>
                    <StyledParentHeaderTableCell sx={{ width: "24%" }}>
                      {formatMessage({ id: `${HEADERS_ID}.rank` })}
                      {!isRecorderMode && (
                        <SortButton
                          direction={sortOrder}
                          onClick={handleSort(EventsStationSortColumn.EquipmentGroupName)}
                          isActive={sortColumn === EventsStationSortColumn.EquipmentGroupName}
                        />
                      )}
                    </StyledParentHeaderTableCell>
                    <StyledParentHeaderTableCell sx={{ width: "24%" }}>
                      {formatMessage({ id: `${HEADERS_ID}.dateTime` })}
                      {!isRecorderMode && (
                        <SortButton
                          direction={sortOrder}
                          onClick={handleSort(EventsStationSortColumn.EventLogDate)}
                          isActive={sortColumn === EventsStationSortColumn.EventLogDate}
                        />
                      )}
                    </StyledParentHeaderTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayEvents.map(event => (
                    <Row key={event.id} row={event} setAckEventId={setAckEventId} setAckDialogOpen={setAckDialogOpen} />
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Box
                        sx={{
                          p: 1,
                          borderTop: "1px solid #EBEBF7",
                          display: "flex",
                          justifyContent: "center",
                          width: "100%",
                        }}
                      >
                        {!isRecorderMode ? (
                          !isLastPage ? (
                            <Button variant="outlined" fullWidth onClick={loadMore} disabled={loading}>
                              <FormattedMessage id="pages.journals.buttons.loadMore" />
                            </Button>
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Typography variant="caption">
                                <FormattedMessage id="pages.journals.captions.noRecords" />
                              </Typography>
                            </Box>
                          )
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </>
        ) : (
          <EmptyPlaceholder icon={<EventBusy />} text={noEvents} />
        )}
      </ProgressOverlay>
    </>
  );
};
